import { RotateCw, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { ConfirmDialog } from '@/components/ConfirmDialog'
import { setLeaveGuard } from '@/lib/leaveGuard'
import { detailActionClass, detailDangerClass } from '@/components/data/DetailPane'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  createPanel,
  deletePanel,
  getPanel,
  PANEL_TYPE_META,
  rotateToken,
  updatePanel,
  type Panel,
} from '@/lib/panels'
import { plural } from '@/lib/utils'

import { PanelPreview } from './PanelPreview'
import { FieldError, Hint, Segmented, UrlRow } from './_shared'
import { BlockInspector } from './blocks/BlockInspector'
import { BlockList } from './blocks/BlockList'
import { StylePanel } from './blocks/StylePanel'
import {
  cleanBlocks,
  DEFAULT_STYLE,
  newBlock,
  validatePanelBlocks,
  type Block,
  type BlockType,
  type PanelStyle,
} from './config-types'

// ────────────────────────────────────────────────────────────────────────────
// Draft model
// ────────────────────────────────────────────────────────────────────────────

const RESERVED = new Set(['router', 'new'])

/** Mirrors the backend _slugify so reserved-name validation matches the server. */
function slugify(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return s.slice(0, 64) || 'panel'
}

const parseScope = (raw: string): string[] =>
  raw
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean)

type Draft = {
  name: string
  scopeRaw: string
  exposeInRouter: boolean
  enabled: boolean
  renderIdentity: 'app' | 'viewer'
  placement: 'content' | 'sidebar'
  blocks: Block[]
  style: PanelStyle
}

/** New-panel starting model: one issue_fields block + default style. */
const newDraft = (): Draft => ({
  name: '',
  scopeRaw: '',
  exposeInRouter: false,
  enabled: true,
  renderIdentity: 'app',
  placement: 'content',
  blocks: [newBlock('issue_fields')],
  style: DEFAULT_STYLE,
})

const fromPanel = (p: Panel): Draft => ({
  name: p.name,
  scopeRaw: p.project_scope.join(', '),
  exposeInRouter: p.expose_in_router,
  enabled: p.enabled,
  renderIdentity: p.render_identity ?? 'app',
  placement: p.placement ?? 'content',
  blocks: p.config?.blocks?.length ? p.config.blocks : [newBlock('issue_fields')],
  style: { ...DEFAULT_STYLE, ...(p.config?.style ?? {}) },
})

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/** Unsaved changes: one per changed setting, one per changed/added/removed block. */
function countChanges(a: Draft, b: Draft): number {
  let n = 0
  if (a.name.trim() !== b.name.trim()) n++
  if (!same(parseScope(a.scopeRaw), parseScope(b.scopeRaw))) n++
  if (a.exposeInRouter !== b.exposeInRouter) n++
  if (a.enabled !== b.enabled) n++
  if (a.renderIdentity !== b.renderIdentity) n++
  if (a.placement !== b.placement) n++
  if (!same(a.style, b.style)) n++
  for (let i = 0; i < Math.max(a.blocks.length, b.blocks.length); i++) {
    if (!same(a.blocks[i], b.blocks[i])) n++
  }
  return n
}

type Errors = { name?: string; block?: { index: number; message: string } }
type Tab = 'block' | 'general' | 'style'

/**
 * Asks before leaving with unsaved changes. The app runs on <BrowserRouter> (no
 * useBlocker), so: beforeunload for reload/close, and a capture-phase click listener
 * for in-app links (menu, breadcrumbs) — it runs before React Router's handlers.
 */
function useUnsavedGuard(changes: number) {
  useEffect(() => {
    if (changes === 0) return
    const message = `You have ${plural(changes, 'unsaved change')}. Leave without saving?`
    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!a || a.target === '_blank') return
      const url = new URL(a.href, window.location.href)
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return
      if (!window.confirm(message)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    window.addEventListener('beforeunload', beforeUnload)
    document.addEventListener('click', onClick, true)
    // navigations without a link (⌘K palette, Sign out) ask through the shared guard
    const unregister = setLeaveGuard(() => window.confirm(message))
    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
      document.removeEventListener('click', onClick, true)
      unregister()
    }
  }, [changes])
}

// ────────────────────────────────────────────────────────────────────────────
// Small pieces
// ────────────────────────────────────────────────────────────────────────────

function Row({ label, htmlFor, children, hint, hintTone }: {
  label: string
  htmlFor?: string
  children: React.ReactNode
  hint?: React.ReactNode
  hintTone?: 'muted' | 'warn'
}) {
  return (
    <div className="grid grid-cols-[150px_minmax(0,1fr)] items-start gap-x-4 border-b border-line-row py-2.5">
      {htmlFor
        ? <label htmlFor={htmlFor} className="pt-1 text-[12.5px] text-text-2">{label}</label>
        : <span className="pt-1 text-[12.5px] text-text-2">{label}</span>}
      <div className="min-w-0">
        {children}
        {hint && <Hint tone={hintTone}>{hint}</Hint>}
      </div>
    </div>
  )
}

const inputClass =
  'h-8 w-full max-w-[420px] rounded-md border border-input bg-surface px-2.5 text-[13px] text-foreground placeholder:text-text-3 aria-[invalid=true]:border-danger'

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export default function PanelForm() {
  const { panelId } = useParams<{ panelId: string }>()
  const isEdit = Boolean(panelId)
  const navigate = useNavigate()

  const [loading, setLoading] = useState(isEdit)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState<Panel | null>(null)

  const [saved, setSaved] = useState<Draft>(newDraft)
  const [draft, setDraft] = useState<Draft>(saved)
  // bumps on every save → the preview re-renders the new saved version
  const [savedVersion, setSavedVersion] = useState(0)

  const [tab, setTab] = useState<Tab>('block')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [errors, setErrors] = useState<Errors>({})
  const [submitting, setSubmitting] = useState(false)

  const [confirmKind, setConfirmKind] = useState<'delete' | 'rotate' | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)

  const changes = useMemo(() => countChanges(draft, saved), [draft, saved])
  useUnsavedGuard(changes)

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }))
  const blocks = draft.blocks
  const safeIndex = blocks.length ? Math.min(selectedIndex, blocks.length - 1) : 0
  const selectedBlock = blocks[safeIndex] as Block | undefined

  // Load existing panel on edit
  useEffect(() => {
    if (!panelId) return
    // /panels/A → /panels/B reuses this component. Without the flag a slow response for A
    // lands after B's and fills B's form with A's config — and Save then PUTs it onto B.
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    getPanel(panelId)
      .then((p) => {
        if (cancelled) return
        const d = fromPanel(p)
        setLoaded(p)
        setSaved(d)
        setDraft(d)
        setSelectedIndex(0)
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [panelId])

  // ── block ops ──────────────────────────────────────────────────────────────
  const setSelectedBlock = (next: Block) =>
    set('blocks', blocks.map((b, idx) => (idx === safeIndex ? next : b)))

  function addBlock(type: BlockType) {
    set('blocks', [...blocks, newBlock(type)])
    setSelectedIndex(blocks.length) // append → new block is at the old length
    setTab('block')
  }

  function removeBlock(i: number) {
    set('blocks', blocks.filter((_, idx) => idx !== i))
    setSelectedIndex((sel) => (i < sel ? sel - 1 : Math.max(0, Math.min(sel, blocks.length - 2))))
  }

  function reorder(from: number, to: number) {
    const next = [...blocks]
    const [b] = next.splice(from, 1)
    next.splice(to, 0, b)
    set('blocks', next)
    setSelectedIndex(to)
  }

  function validate(d: Draft): Errors {
    const errs: Errors = {}
    const n = d.name.trim()
    if (!n) errs.name = 'Name is required.'
    else if (RESERVED.has(slugify(n))) errs.name = `"${slugify(n)}" is a reserved name.`
    const blockErr = validatePanelBlocks(d.blocks)
    if (blockErr) errs.block = blockErr
    return errs
  }

  async function save() {
    const errs = validate(draft)
    setErrors(errs)
    if (errs.name) {
      setTab('general')
      setTimeout(() => document.getElementById('panel-name')?.focus(), 0)
      return
    }
    if (errs.block) {
      if (errs.block.index >= 0) setSelectedIndex(errs.block.index)
      setTab('block')
      return
    }
    const body: Partial<Panel> = {
      name: draft.name.trim(),
      config: { blocks: cleanBlocks(draft.blocks), style: draft.style },
      project_scope: parseScope(draft.scopeRaw),
      expose_in_router: draft.exposeInRouter,
      enabled: draft.enabled,
      render_identity: draft.renderIdentity,
      placement: draft.placement,
    }
    setSubmitting(true)
    try {
      if (isEdit && panelId) {
        const updated = await updatePanel(panelId, body)
        const d = fromPanel(updated)
        setLoaded(updated)
        setSaved(d)
        setDraft(d)
        setSavedVersion((v) => v + 1)
        toast.success('Panel saved')
      } else {
        const created = await createPanel(body)
        setSaved(draft) // nothing left unsaved — no guard on the way to the editor
        toast.success('Panel created')
        navigate(`/panels/${created.id}`)
      }
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  async function runConfirm() {
    if (!panelId || !confirmKind) return
    setConfirmBusy(true)
    try {
      if (confirmKind === 'delete') {
        await deletePanel(panelId)
        setSaved(draft)
        toast.success('Panel deleted')
        navigate('/panels')
      } else {
        const rotated = await rotateToken(panelId)
        setLoaded(rotated)
        toast.success('Token rotated — old URLs no longer work')
        setConfirmKind(null)
      }
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setConfirmBusy(false)
    }
  }

  const crumb = (
    <>
      <Link to="/panels" className="text-text-2 transition-colors hover:text-foreground">Panels</Link>
      <span aria-hidden>/</span>
    </>
  )

  if (loading || loadError) {
    return (
      <Page title={loadError ? 'Panel' : 'Loading…'} crumb={crumb}>
        {loadError
          ? <p role="alert" className="px-4 py-4 text-[13px] text-danger">{loadError}</p>
          : (
            <div className="space-y-2 p-4" aria-busy="true" aria-label="Loading panel">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-[22px] w-full" />)}
            </div>
          )}
      </Page>
    )
  }

  const scope = parseScope(draft.scopeRaw)
  const tabs: { key: Tab; label: string }[] = [
    { key: 'block', label: 'Block' },
    { key: 'general', label: 'General' },
    { key: 'style', label: 'Style' },
  ]

  return (
    <Page
      title={loaded?.name ?? 'New panel'}
      crumb={crumb}
      context={(
        <>
          <span className="truncate rounded-full bg-surface-active px-2.5 py-0.5 text-[11.5px] text-text-2">
            {draft.placement} · <span className="font-mono">{scope.length ? scope.join(', ') : 'any project'}</span>
          </span>
          <label className="flex shrink-0 items-center gap-1.5 text-[12px] text-text-2">
            <Switch checked={draft.enabled} onCheckedChange={(v) => set('enabled', v)} aria-label="Panel enabled" />
            {draft.enabled ? 'enabled' : 'disabled'}
          </label>
          {changes > 0 && (
            <span className="ml-auto shrink-0 text-[12px] text-warn" aria-live="polite">
              <span aria-hidden>● </span>{plural(changes, 'unsaved change')}
            </span>
          )}
        </>
      )}
      actions={(
        <>
          <button
            type="button"
            onClick={() => { setDraft(saved); setErrors({}) }}
            disabled={changes === 0 || submitting}
            className={detailActionClass}
          >
            Discard
          </button>
          <Button size="sm" onClick={() => void save()} disabled={submitting || (isEdit && changes === 0)}>
            {submitting ? 'Saving…' : isEdit ? 'Save' : 'Create panel'}
          </Button>
        </>
      )}
    >
      <div className="grid min-h-0 flex-1 grid-cols-[230px_minmax(0,1fr)_400px]">
        {/* ── left: blocks ─────────────────────────────────────────── */}
        <div className="min-h-0 overflow-y-auto border-r border-line px-3 py-3">
          <BlockList
            blocks={blocks}
            selectedIndex={safeIndex}
            onSelect={(i) => { setSelectedIndex(i); setTab('block') }}
            onAdd={addBlock}
            onReorder={reorder}
          />
        </div>

        {/* ── centre: block / general / style ──────────────────────── */}
        <div className="flex min-h-0 min-w-0 flex-col">
          <div role="tablist" aria-label="Panel settings" className="flex shrink-0 gap-5 border-b border-line px-4">
            {tabs.map((t) => (
              <button
                key={t.key}
                id={`panel-tab-${t.key}`}
                role="tab"
                type="button"
                aria-selected={tab === t.key}
                aria-controls="panel-tabpanel"
                tabIndex={tab === t.key ? 0 : -1}
                onClick={() => setTab(t.key)}
                onKeyDown={(e) => {
                  const i = tabs.findIndex((x) => x.key === tab)
                  const j = e.key === 'ArrowRight' ? (i + 1) % tabs.length
                    : e.key === 'ArrowLeft' ? (i - 1 + tabs.length) % tabs.length : null
                  if (j === null) return
                  e.preventDefault()
                  setTab(tabs[j].key)
                  document.getElementById(`panel-tab-${tabs[j].key}`)?.focus()
                }}
                className={[
                  'relative py-2.5 text-[13px] transition-colors focus-visible:-outline-offset-2',
                  tab === t.key ? 'text-foreground' : 'text-text-2 hover:text-foreground',
                ].join(' ')}
              >
                {t.label}
                {tab === t.key && <span aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" />}
              </button>
            ))}
          </div>
          <div
            role="tabpanel"
            id="panel-tabpanel"
            aria-labelledby={`panel-tab-${tab}`}
            className="min-h-0 flex-1 overflow-y-auto px-4 py-3"
          >
            <SectionBoundary key={tab} label={tabs.find((t) => t.key === tab)!.label}>
              {tab === 'block' && (selectedBlock
                ? (
                  <div>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h2 className="text-[14px] font-semibold text-foreground">
                        {PANEL_TYPE_META[selectedBlock.type]?.label ?? selectedBlock.type}
                      </h2>
                      <button
                        type="button"
                        onClick={() => removeBlock(safeIndex)}
                        className="text-[12px] text-danger underline-offset-2 hover:underline"
                      >
                        Remove block
                      </button>
                    </div>
                    <BlockInspector block={selectedBlock} onChange={setSelectedBlock} />
                    <FieldError>{errors.block?.message}</FieldError>
                  </div>
                )
                : <p className="text-[13px] text-text-2">No blocks yet — add one on the left.</p>)}

              {tab === 'general' && (
                <div>
                  <Row label="Name" htmlFor="panel-name">
                    <input
                      id="panel-name"
                      value={draft.name}
                      onChange={(e) => set('name', e.target.value)}
                      onBlur={() => setErrors((p) => ({ ...p, name: validate(draft).name }))}
                      placeholder="e.g. CM Info"
                      aria-invalid={Boolean(errors.name)}
                      className={inputClass}
                    />
                    <FieldError>{errors.name}</FieldError>
                  </Row>
                  <Row label="Project scope" htmlFor="panel-scope" hint="Comma-separated project keys. Empty = any project.">
                    <input
                      id="panel-scope"
                      value={draft.scopeRaw}
                      onChange={(e) => set('scopeRaw', e.target.value)}
                      placeholder="e.g. CM, SEC"
                      className={`${inputClass} font-mono`}
                    />
                  </Row>
                  <Row
                    label="Placement"
                    hint="content — the main area of the work item view, under the description. sidebar — the right column (Forge panel only; SR4JC and the embed ignore it)."
                  >
                    <Segmented
                      ariaLabel="Placement"
                      value={draft.placement}
                      options={[{ value: 'content', label: 'content' }, { value: 'sidebar', label: 'sidebar' }]}
                      onChange={(v) => set('placement', v)}
                    />
                  </Row>
                  <Row
                    label="Render as"
                    hint="app — data is read as the app, the same for everyone. viewer — read with the viewing user’s permissions (works only in the Forge panel)."
                  >
                    <Segmented
                      ariaLabel="Render as"
                      value={draft.renderIdentity}
                      options={[{ value: 'app', label: 'app' }, { value: 'viewer', label: 'viewer' }]}
                      onChange={(v) => set('renderIdentity', v)}
                    />
                  </Row>
                  <Row
                    label="Expose in router"
                    hint="The Connect channel is public — the router token is visible in the public descriptor."
                    hintTone="warn"
                  >
                    <Switch checked={draft.exposeInRouter} onCheckedChange={(v) => set('exposeInRouter', v)} aria-label="Expose in router" />
                  </Row>
                  <Row label="Enabled">
                    <Switch checked={draft.enabled} onCheckedChange={(v) => set('enabled', v)} aria-label="Enabled" />
                  </Row>
                  {isEdit && (
                    <div className="mt-6">
                      <button type="button" onClick={() => setConfirmKind('delete')} className={detailDangerClass}>
                        <Trash2 aria-hidden className="size-3.5" />
                        Delete panel
                      </button>
                      <Hint>Deletes the panel for good; embedded URLs start returning 404.</Hint>
                    </div>
                  )}
                </div>
              )}

              {tab === 'style' && (
                <StylePanel style={draft.style} onChange={(s) => set('style', s)} />
              )}
            </SectionBoundary>
          </div>
        </div>

        {/* ── right: preview + embed ───────────────────────────────── */}
        <section aria-label="Preview" className="min-h-0 overflow-y-auto border-l border-line bg-pane px-4 py-3">
          <h2 className="mb-2 text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">Preview</h2>
          {loaded
            ? (
              <SectionBoundary label="Preview">
                <PanelPreview panelId={loaded.id} version={savedVersion} auto />
                {changes > 0 && (
                  <Hint>Edits made since the last save aren’t in the preview — save to see them.</Hint>
                )}
              </SectionBoundary>
            )
            : <p className="text-[12.5px] text-text-2">Save the panel to preview it on a test issue.</p>}

          <details className="group mt-5">
            <summary className="cursor-pointer select-none text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">
              <span>Embed</span>
              <span className="ml-2 normal-case tracking-normal">SR4JC URL, direct URL, token</span>
            </summary>
            {loaded
              ? (
                <div className="mt-2.5 space-y-2.5">
                  <UrlRow label="SR4JC URL" value={loaded.sr_url} />
                  <Hint>
                    Paste as <code>Single URL</code> in an SR4JC WebPanel fragment; the issue key is resolved
                    via the Adaptavist Bridge.
                  </Hint>
                  <UrlRow label="Direct URL" value={loaded.url_template} />
                  <button type="button" onClick={() => setConfirmKind('rotate')} className={detailActionClass}>
                    <RotateCw aria-hidden className="size-3.5" />
                    Rotate token
                  </button>
                </div>
              )
              : <p className="mt-2 text-[12.5px] text-text-2">Save the panel to get its embed URLs and token.</p>}
          </details>
        </section>
      </div>

      {confirmKind === 'rotate' && (
        <ConfirmDialog
          title="Rotate token"
          body="Old URLs (SR4JC fragments already deployed) will stop working. Continue?"
          confirmLabel="Rotate"
          busy={confirmBusy}
          onConfirm={() => void runConfirm()}
          onClose={() => setConfirmKind(null)}
        />
      )}
      {confirmKind === 'delete' && (
        <ConfirmDialog
          title="Delete panel"
          body="This permanently deletes the panel. Any embedded URLs will 404. This cannot be undone."
          confirmLabel="Delete"
          destructive
          busy={confirmBusy}
          onConfirm={() => void runConfirm()}
          onClose={() => setConfirmKind(null)}
        />
      )}
    </Page>
  )
}
